import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { useApiClient } from '../../hooks/useApiClient';
import type { ManagedUser, UserRole } from '../../types';

interface CreateUserForm {
  username: string;
  displayName: string;
  role: UserRole;
  isActive: boolean;
}

const DEFAULT_FORM: CreateUserForm = {
  username: '',
  displayName: '',
  role: 'operator',
  isActive: true,
};

export default function UsersPage() {
  const apiClient = useApiClient();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateUserForm>(DEFAULT_FORM);
  const [generatedSecret, setGeneratedSecret] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient('/api/admin/users');
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const payload = (await response.json()) as ManagedUser[];
      setUsers(payload);
    } catch (fetchError) {
      console.error(fetchError);
      setError('Unable to load users from the backend.');
    } finally {
      setIsLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => a.username.localeCompare(b.username, undefined, { sensitivity: 'base' })),
    [users],
  );

  const resetForm = () => {
    setForm(DEFAULT_FORM);
  };

  const handleCreateUser = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.username.trim()) {
      toast.error('Username is required');
      return;
    }
    setIsSubmitting(true);
    setGeneratedSecret(null);
    try {
      const response = await apiClient('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({
          username: form.username.trim(),
          displayName: form.displayName.trim() || undefined,
          role: form.role,
          isActive: form.isActive,
        }),
      });
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || 'Unable to create user');
      }
      const payload = await response.json();
      const { user, password } = payload as { user: ManagedUser; password?: string | null };
      setUsers((prev) => [...prev, user]);
      if (password) {
        setGeneratedSecret(password);
      }
      toast.success(`Created user ${user.username}`);
      resetForm();
    } catch (submitError) {
      console.error(submitError);
      toast.error(submitError instanceof Error ? submitError.message : 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (user: ManagedUser) => {
    try {
      const response = await apiClient(`/api/admin/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !user.isActive }),
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const updated = (await response.json()) as ManagedUser;
      setUsers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      toast.success(`${updated.username} ${updated.isActive ? 'enabled' : 'disabled'}`);
    } catch (toggleError) {
      console.error(toggleError);
      toast.error('Unable to update user status');
    }
  };

  const handleResetPassword = async (user: ManagedUser) => {
    try {
      const response = await apiClient(`/api/admin/users/${user.id}/reset-password`, { method: 'POST' });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const payload = await response.json();
      const password = payload?.password as string | undefined;
      if (password) {
        setGeneratedSecret(password);
        toast.success(`Password reset for ${user.username}`);
      }
    } catch (resetError) {
      console.error(resetError);
      toast.error('Unable to reset password');
    }
  };

  const handleRevokeSessions = async (user: ManagedUser) => {
    try {
      const response = await apiClient(`/api/admin/users/${user.id}/sessions/revoke`, { method: 'POST' });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      toast.success(`Revoked active sessions for ${user.username}`);
    } catch (revokeError) {
      console.error(revokeError);
      toast.error('Unable to revoke sessions');
    }
  };

  return (
    <div className="admin-section">
      <header className="admin-section-header">
        <div>
          <h1>Users</h1>
          <p>Manage administrator, operator, and spectator accounts.</p>
        </div>
      </header>

      <form className="admin-form" onSubmit={handleCreateUser}>
        <h2>Create User</h2>
        <div className="admin-form-grid">
          <label>
            <span>Username</span>
            <input
              type="text"
              value={form.username}
              onChange={(event) => setForm((prev) => ({ ...prev, username: event.target.value }))}
              placeholder="operator@example"
              required
              disabled={isSubmitting}
            />
          </label>
          <label>
            <span>Display Name</span>
            <input
              type="text"
              value={form.displayName}
              onChange={(event) => setForm((prev) => ({ ...prev, displayName: event.target.value }))}
              placeholder="Visible label (optional)"
              disabled={isSubmitting}
            />
          </label>
          <label>
            <span>Role</span>
            <select
              value={form.role}
              onChange={(event) => setForm((prev) => ({ ...prev, role: event.target.value as UserRole }))}
              disabled={isSubmitting}
            >
              <option value="admin">Admin</option>
              <option value="operator">Operator</option>
              <option value="spectator">Spectator</option>
            </select>
          </label>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
              disabled={isSubmitting}
            />
            <span>Active immediately</span>
          </label>
        </div>
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creating…' : 'Create User'}
        </button>
        {generatedSecret ? (
          <div className="admin-secret" role="alert">
            <strong>New credential:</strong>
            <code>{generatedSecret}</code>
            <span>Share this one-time password securely with the user.</span>
          </div>
        ) : null}
      </form>

      <section className="admin-list">
        <h2>Existing Users</h2>
        {isLoading ? <p>Loading users…</p> : null}
        {error ? <p className="admin-error">{error}</p> : null}
        {!isLoading && !error ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="admin-cell-primary">{user.username}</div>
                      {user.displayName ? <div className="admin-cell-subtle">{user.displayName}</div> : null}
                    </td>
                    <td>{user.role}</td>
                    <td>
                      <span className={user.isActive ? 'badge badge-success' : 'badge badge-neutral'}>
                        {user.isActive ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '—'}</td>
                    <td>{new Date(user.createdAt).toLocaleString()}</td>
                    <td className="admin-actions">
                      <button type="button" onClick={() => handleToggleActive(user)}>
                        {user.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <button type="button" onClick={() => void handleResetPassword(user)}>
                        Reset Password
                      </button>
                      <button type="button" onClick={() => void handleRevokeSessions(user)}>
                        Revoke Sessions
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sortedUsers.length === 0 ? <p>No users yet.</p> : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
